import { useEffect, useRef, useState } from "react";
import { ArrowDownUp, MessageSquare, Pin, PinOff, Send, Trash2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CoachListSection from "./CoachListSection";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import LoadFailedCard from "./LoadFailedCard";
import { FailureCard, SavedCard } from "./GoalNotice";
import { CHIP, EmptyList, HEADING, ICON_BTN, ListLoading, NAVY_BTN, NOTES_FRAME, ROW_FRAME, SelectBox, TEXTAREA } from "./CoachNotesKit";
import { FEEDBACK_SORTS, sortRows } from "../lib/coachListSort";

/**
 * Issue #460 — the Coach Portal's Feedback tab, lifted out of
 * `CoachPortal.jsx` whole (see `CoachGoalsPanel.jsx` for why all four
 * panels moved). Behaviour, testids and copy are unchanged in the move.
 *
 * NEW HERE: the sort control, and `focusNonce`.
 *
 * `focusNonce` is what makes the ribbon's "Provide feedback" shortcut land
 * in the FLOW rather than merely on the tab that contains it: the page
 * bumps a counter, this effect puts the cursor in the composer. A counter
 * rather than a boolean because the gesture repeats — a coach who clicks
 * the shortcut, scrolls away and clicks it again means it both times, and a
 * boolean that is already `true` fires nothing the second time.
 *
 * THE REQUEST IS SPENT, for `useCreateDialog`'s reason (#79). A Radix
 * `TabsContent` unmounts this panel when the coach leaves the tab, so an
 * unconsumed nonce would still be truthy on the next mount and grab the
 * cursor every subsequent time they opened Feedback — a shortcut clicked
 * once quietly becoming a permanent behaviour change.
 */
export default function CoachFeedbackPanel(props) {
  // #1221 slice 3: the coach's player page passes `appearance="r5"` and gets the
  // kit version below. Unset (the flag-off page, admin) is the shipped panel.
  if (props.appearance === "r5") return <CoachFeedbackPanelR5 {...props} />;
  return <ShippedCoachFeedbackPanel {...props} />;
}

function ShippedCoachFeedbackPanel({
  notes,
  sort,
  onSortChange,
  filterCat,
  onFilterChange,
  categories,
  canMutateNote,
  onTogglePin,
  onDelete,
  feedback,
  onFeedbackChange,
  category,
  onCategoryChange,
  onAdd,
  focusNonce,
  onFocusHandled,
}) {
  const composer = useRef(null);
  useEffect(() => {
    if (!focusNonce) return;
    composer.current?.focus();
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  const catMeta = (v) => categories.find((c) => c.value === v) || categories[0];
  const visible = filterCat === "all" ? notes : notes.filter((n) => (n.category || "general") === filterCat);
  const rows = sortRows(FEEDBACK_SORTS, sort, visible);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <CoachListSection
          title="Coach feedback"
          sorts={FEEDBACK_SORTS}
          sortValue={sort}
          onSortChange={onSortChange}
          sortTestId="cp-notes-sort"
          listTestId="portal-notes"
          controls={
            <Select value={filterCat} onValueChange={onFilterChange}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="cp-notes-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          }
        >
          {rows.length === 0 && <p className="text-sm text-slate-500">No feedback in this filter.</p>}
          {rows.map((n) => {
            const meta = catMeta(n.category);
            return (
              <div key={n.note_id} className="p-3 rounded-lg border border-slate-200 group relative">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <Badge className="text-[10px]" style={{ background: `${meta.color}15`, color: meta.color }}>{meta.label}</Badge>
                  {canMutateNote(n) && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => onTogglePin(n)} title={n.pinned ? "Unpin" : "Pin"} className="text-slate-500 hover:text-slate-900" data-testid={`cp-note-pin-${n.note_id}`}>
                        {n.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                      </button>
                      <button onClick={() => onDelete(n)} title="Delete" className="text-slate-500 hover:text-red-600" data-testid={`cp-note-del-${n.note_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {n.pinned && <div className="text-[10px] uppercase tracking-wider text-[#D4A017] font-semibold mb-1">Pinned</div>}
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.feedback}</div>
                <div className="text-xs text-slate-500 mt-1.5">— {n.coach_name} · {n.created_date?.slice(0, 10)}</div>
              </div>
            );
          })}
        </CoachListSection>

        <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
          <Textarea ref={composer} value={feedback} onChange={(e) => onFeedbackChange(e.target.value)} placeholder="Add feedback…" rows={3} data-testid="feedback-input" />
          {/* Issue #141: this row had no wrap and a fixed w-36 Select beside
              a flex-1 Button whose nowrap label ("Add feedback" + icon)
              can't shrink — the sum doesn't fit the Feedback tab's available
              width at 390px, and (since nothing between here and the
              `lg:grid-cols-4` page grid sets overflow or min-w-0) that
              pushes the whole grid track wide. */}
          <div className="flex flex-wrap gap-2">
            <Select value={category} onValueChange={onCategoryChange}>
              <SelectTrigger className="w-full sm:w-36 h-10" data-testid="cp-note-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={onAdd} className="w-full sm:flex-1" style={{ background: "#0B2E59" }} data-testid="feedback-add">
              <Send size={14} className="mr-2" /> Add feedback
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 3 (D240, D251) — `CoachPlayerFeedback.dc.html`'s first half, in the kit.
//
// "Feedback to [name]": a category filter and a sort, the notes (pinned first,
// then the chosen order), a line saying who reads what the coach writes, and the
// composer. Pin and delete are ON EVERY NOTE the coach may change (a phone has
// no hover). Loading, empty, filtered-empty and failed are four different nodes
// (D53 ruling 2). A failed add shows in place and keeps the text (D57); deleting
// asks in the page (D223.1, `ConfirmDeleteDialog`), never through the browser's native prompt.
//
// The writes are the page's: `onAdd()` and `onRemove(note)` return a promise
// that rejects on failure; this panel shows the failure where it happened. No
// `Card`, `Button`, `Select` or `Textarea` from `ui/` is rendered here.
// ---------------------------------------------------------------------------

function NoteRow({ n, label, canMutate, onTogglePin, onRequestDelete }) {
  return (
    <div data-testid={`cp-note-${n.note_id}`} style={ROW_FRAME}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", flexGrow: 1, minWidth: 0 }}>
          <span style={CHIP}>{label}</span>
        </div>
        {canMutate && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button type="button" aria-label={n.pinned ? "Unpin" : "Pin"} aria-pressed={n.pinned ? "true" : "false"} onClick={() => onTogglePin(n)} data-testid={`cp-note-pin-${n.note_id}`} style={ICON_BTN}>
              {n.pinned ? <PinOff size={16} color="var(--nw-navy)" aria-hidden="true" /> : <Pin size={16} color="var(--nw-navy)" aria-hidden="true" />}
            </button>
            <button type="button" aria-label="Delete note" onClick={() => onRequestDelete(n)} data-testid={`cp-note-del-${n.note_id}`} style={ICON_BTN}>
              <Trash2 size={16} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {n.pinned && (
        <div data-testid={`cp-note-pinned-${n.note_id}`} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-navy)" }}>
          Pinned
        </div>
      )}
      <div style={{ fontSize: 15, lineHeight: 1.5, color: "var(--nw-ink)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{n.feedback}</div>
      <div style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>— {n.coach_name} · {n.created_date?.slice(0, 10)}</div>
    </div>
  );
}

function CoachFeedbackPanelR5({
  status = "loaded",
  firstName,
  notes,
  sort,
  onSortChange,
  filterCat,
  onFilterChange,
  categories,
  canMutateNote,
  onTogglePin,
  onRemove,
  feedback,
  onFeedbackChange,
  category,
  onCategoryChange,
  onAdd,
  onRetry,
  focusNonce,
  onFocusHandled,
}) {
  const composer = useRef(null);
  useEffect(() => {
    if (!focusNonce) return;
    composer.current?.focus();
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [addError, setAddError] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const labelOf = (v) => {
    const found = categories.find((c) => c.value === v) || categories.find((c) => c.value === "general");
    const raw = found ? found.label : v || "general";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };
  const visible = filterCat === "all" ? notes : notes.filter((n) => (n.category || "general") === filterCat);
  const rows = sortRows(FEEDBACK_SORTS, sort, visible);

  const add = async () => {
    if (adding || !feedback.trim()) return;
    setAdding(true);
    setAdded(false);
    setAddError(false);
    try {
      await onAdd();
      setAdded(true);
    } catch {
      setAddError(true);
    }
    setAdding(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onRemove(deleteTarget);
      setDeleteTarget(null);
    } catch {
      setDeleteError("Nothing has been lost — we just couldn't reach the server.");
    }
    setDeleting(false);
  };

  const heading = (
    <h3 tabIndex={-1} data-focus-after-delete style={{ ...HEADING, outline: "none" }}>
      Feedback to {firstName}
    </h3>
  );

  if (status === "loading") {
    return (
      <div style={NOTES_FRAME}>
        <div aria-hidden="true" style={{ height: 14, width: "40%", borderRadius: 6, background: "var(--nw-line)" }} />
        <ListLoading testId="cp-notes-loading" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div style={NOTES_FRAME}>
        {heading}
        <LoadFailedCard title="Couldn't load this player's feedback" onRetry={onRetry} testId="cp-notes-load-failed" />
      </div>
    );
  }

  return (
    <div style={NOTES_FRAME}>
      {heading}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <SelectBox
          value={filterCat}
          onChange={onFilterChange}
          ariaLabel="Filter feedback by category"
          testId="cp-notes-filter"
          options={[{ value: "all", label: "All categories" }, ...categories.map((c) => ({ value: c.value, label: c.label }))]}
        />
        <SelectBox
          icon={ArrowDownUp}
          value={sort}
          onChange={onSortChange}
          ariaLabel="Sort feedback"
          testId="cp-notes-sort"
          options={FEEDBACK_SORTS.map((s) => ({ value: s.value, label: s.label }))}
        />
      </div>

      {notes.length === 0 ? (
        <EmptyList testId="portal-notes" icon={MessageSquare} title={`No feedback for ${firstName} yet.`} hint="What you add below, they read." />
      ) : rows.length === 0 ? (
        <EmptyList testId="portal-notes" title="No feedback in this filter." />
      ) : (
        <div data-testid="portal-notes" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((n) => (
            <NoteRow
              key={n.note_id}
              n={n}
              label={labelOf(n.category || "general")}
              canMutate={canMutateNote(n)}
              onTogglePin={onTogglePin}
              onRequestDelete={(note) => {
                setDeleteError(null);
                setDeleteTarget(note);
              }}
            />
          ))}
        </div>
      )}

      <div data-testid="cp-notes-divider" aria-hidden="true" style={{ borderTopWidth: 1, borderTopStyle: "solid", borderTopColor: "var(--nw-line)" }} />
      <p data-testid="cp-notes-reader-line" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
        {firstName} reads what you write here, on their Development page.
      </p>
      <textarea
        ref={composer}
        value={feedback}
        onChange={(e) => {
          setAdded(false);
          onFeedbackChange(e.target.value);
        }}
        placeholder="Add feedback…"
        aria-label="Add feedback"
        rows={3}
        data-testid="feedback-input"
        style={TEXTAREA}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label htmlFor="cp-note-category" style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>
          Category
        </label>
        <SelectBox
          id="cp-note-category"
          value={category}
          onChange={onCategoryChange}
          ariaLabel="Category"
          testId="cp-note-category"
          options={categories.map((c) => ({ value: c.value, label: c.label }))}
        />
      </div>
      {addError && <FailureCard data-testid="cp-notes-add-error" title="Failed to add feedback" body="Your note is still in the box. Try again." />}
      {added && !addError && <SavedCard data-testid="cp-notes-added">Feedback added</SavedCard>}
      <button type="button" onClick={add} disabled={adding} data-testid="feedback-add" style={{ ...NAVY_BTN, width: "100%", opacity: adding ? 0.7 : 1, cursor: adding ? "not-allowed" : "pointer" }}>
        <Send size={15} aria-hidden="true" />
        Add feedback
      </button>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title="Delete this feedback note?"
        confirmLabel="Delete note"
        testId="cp-note-delete"
        deleting={deleting}
        error={deleteError}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
